import { GetServerSidePropsResult } from 'next';
import * as Sentry from '@sentry/nextjs';

type ErrorHandlingOptions = {
  shouldSendToSentry: boolean;
  requireReStart: boolean;
};

/**
 * エラーをハンドリングするためのクラス。
 * 例外のスローを制御し、追加の情報を提供することができます。
 *
 * @property name - エラーの名前。`"HandledError"`に固定されます。
 * @property options - エラーのハンドリングに関するオプション。
 *
 * @param cause - 発生したエラーの原因となったエラーオブジェクト。
 * @param options - エラーのハンドリングに関するオプション。`shouldSendToSentry`はSentryにエラーを送信するかどうかを示し、`requireReStart`は回復不能かどうかを示します。
 */
export class HandledError extends Error {
  readonly name = 'HandledError';
  readonly options: ErrorHandlingOptions;

  constructor(error: Error | string, options: ErrorHandlingOptions) {
    const message = typeof error === 'string' ? error : error.message;

    super(message, {
      cause: typeof error === 'string' ? undefined : error,
    });

    this.options = options;
  }
}

export const isHandledError = (error: Error): error is HandledError => {
  return error.name === 'HandledError';
};

export class NotFoundError extends Error {
  readonly name = 'NotFoundError';

  constructor(cause?: Error) {
    super(cause?.message, {
      cause,
    });
  }
}

export const isNotFoundError = (error: Error): error is NotFoundError => {
  return error.name === 'NotFoundError';
};

export type ErrorType = 'retryable' | 'require-restart';

export const getErrorType = (error: HandledError): ErrorType => {
  if (error.options.requireReStart) {
    return 'require-restart';
  }
  return 'retryable';
};

// for SSR
export const handleErrorForSSR = (
  error: HandledError | NotFoundError,
): GetServerSidePropsResult<{ error: ErrorType }> => {
  if (isNotFoundError(error)) {
    return {
      notFound: true,
    };
  }
  if (error.options.shouldSendToSentry) {
    Sentry.captureException(error);
  }
  const errorType = getErrorType(error);
  return {
    props: {
      error: errorType,
    },
  };
};
